<template>
  <div class="bg-pink-100 p-4 rounded-md max-h-[90vh] overflow-y-auto">
    <h2 class="text-xl font-bold mb-4 top-0 bg-pink-100 z-10">Verordnung häuslicher Krankenpflege (Muster 12)</h2>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      <div>
        <label class="flex items-center">
          <input v-model="formData.erstverordnung" type="checkbox" class="mr-2" />
          Erstverordnung
        </label>
      </div>
      <div>
        <label class="flex items-center">
          <input v-model="formData.folgeverordnung" type="checkbox" class="mr-2" />
          Folgeverordnung
        </label>
      </div>
      <div>
        <label class="flex items-center">
          <input v-model="formData.unfall" type="checkbox" class="mr-2" />
          Unfall
        </label>
      </div>
      <div>
        <label class="flex items-center">
          <input v-model="formData.ser" type="checkbox" class="mr-2" />
          SER
        </label>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4 mb-4">
      <div>
        <label>Vom:</label>
        <input v-model="formData.periodStart" type="date" class="p-2 border rounded" />
      </div>
      <div>
        <label>Bis:</label>
        <input v-model="formData.periodEnd" type="date" class="p-2 border rounded" />
      </div>
    </div>

    <div class="mb-4 border-t-2 border-red-500 pt-4">
      <h3 class="font-bold text-lg mb-2">Behandlungspflege</h3>
      
      <div class="mb-4">
        <h4 class="font-semibold">Medikamentengabe, Präparate</h4>
        <input v-model="formData.medicationInput" type="text" class="p-2 border rounded w-full mb-2" />
        
        <div class="grid grid-cols-3 gap-4 mb-2">
          <div class="col-span-2 flex items-center">
            <input v-model="formData.medicationPreparation" type="checkbox" class="mr-2" />
            <label>Herrichten der Medikamentenbox</label>
          </div>
          <FrequencyAndDuration 
            :items="formData.medicationPreparationFrequency"
            @update="updateFrequencyAndDuration('medicationPreparationFrequency', $event)"
          />
        </div>

        <div class="grid grid-cols-3 gap-4 mb-2">
          <div class="col-span-2 flex items-center">
            <input v-model="formData.medicationAdministration" type="checkbox" class="mr-2" />
            <label>Medikamentengabe</label>
          </div>
          <FrequencyAndDuration 
            :items="formData.medicationAdministrationFrequency"
            @update="updateFrequencyAndDuration('medicationAdministrationFrequency', $event)"
          />
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div class="col-span-2 flex items-center space-x-4">
            <label class="flex items-center">
              <input v-model="formData.injection" type="checkbox" class="mr-2" />
              Injektion
            </label>
            <label class="flex items-center">
              <input v-model="formData.injectionPreparation" type="checkbox" class="mr-2" />
              herrichten
            </label>
            <label class="flex items-center">
              <input v-model="formData.injectionIntramuscular" type="checkbox" class="mr-2" />
              intramuskulär
            </label>
            <label class="flex items-center">
              <input v-model="formData.injectionSubcutaneous" type="checkbox" class="mr-2" />
              subkutan
            </label>
          </div>
          <FrequencyAndDuration 
            :items="formData.injectionFrequency"
            @update="updateFrequencyAndDuration('injectionFrequency', $event)"
          />
        </div>
      </div>

      <div class="mb-4">
        <h4 class="font-semibold">Blutzuckermessung</h4>
        <div class="grid grid-cols-3 gap-4">
          <div class="col-span-2 flex items-center space-x-4">
            <label class="flex items-center">
              <input v-model="formData.bloodSugarInitial" type="checkbox" class="mr-2" />
              Erst- oder Neueinstellung
            </label>
            <label class="flex items-center">
              <input v-model="formData.bloodSugarIntensive" type="checkbox" class="mr-2" />
              bei intensivierter Insulintherapie
            </label>
          </div>
          <FrequencyAndDuration 
            :items="formData.bloodSugarFrequency"
            @update="updateFrequencyAndDuration('bloodSugarFrequency', $event)"
          />
        </div>
      </div>

      <div class="mb-4">
        <h4 class="font-semibold">Kompressionsbehandlung</h4>
        <div class="grid grid-cols-3 gap-4 mb-2">
          <div class="col-span-2 flex items-center space-x-4">
            <label class="flex items-center">
              <input v-model="formData.compressionStockingsPutOn" type="checkbox" class="mr-2" />
              Kompressionsstrümpfe anziehen
            </label>
            <label class="flex items-center">
              <input v-model="formData.compressionStockingsTakeOff" type="checkbox" class="mr-2" />
              Kompressionsstrümpfe ausziehen
            </label>
          </div>
          <FrequencyAndDuration 
            :items="formData.compressionStockingsFrequency"
            @update="updateFrequencyAndDuration('compressionStockingsFrequency', $event)"
          />
        </div>
        <div class="grid grid-cols-3 gap-4 mb-2">
          <div class="col-span-2 flex items-center space-x-4">
            <label class="flex items-center">
              <input v-model="formData.compressionBandageApply" type="checkbox" class="mr-2" />
              Kompressionsverbände anlegen
            </label>
            <label class="flex items-center">
              <input v-model="formData.compressionBandageRemove" type="checkbox" class="mr-2" />
              Kompressionsverbände abnehmen
            </label>
          </div>
          <FrequencyAndDuration 
            :items="formData.compressionBandageFrequency"
            @update="updateFrequencyAndDuration('compressionBandageFrequency', $event)"
          />
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div class="col-span-2">
            <label class="flex items-center">
              <input v-model="formData.supportiveBandages" type="checkbox" class="mr-2" />
              Stützende und stabilisierende Verbände
            </label>
            <input v-model="formData.supportiveBandagesType" type="text" placeholder="Art" class="p-2 border rounded w-full mt-2" />
          </div>
          <FrequencyAndDuration 
            :items="formData.supportiveBandagesFrequency"
            @update="updateFrequencyAndDuration('supportiveBandagesFrequency', $event)"
          />
        </div>
        <div class="flex space-x-4 mt-2">
          <label class="flex items-center">
            <input v-model="formData.compressionRight" type="checkbox" class="mr-2" />
            rechts
          </label>
          <label class="flex items-center">
            <input v-model="formData.compressionLeft" type="checkbox" class="mr-2" />
            links
          </label>
          <label class="flex items-center">
            <input v-model="formData.compressionBoth" type="checkbox" class="mr-2" />
            beidseitig
          </label>
        </div>
      </div>

      <div class="mb-4">
        <h4 class="font-semibold">Wundversorgung</h4>
        <input v-model="formData.woundType" type="text" placeholder="Wundart" class="p-2 border rounded w-full mb-2" />
        <div class="grid grid-cols-3 gap-4 mb-2">
          <input v-model="formData.woundLocation" type="text" placeholder="Lokalisation" class="p-2 border rounded w-full" />
          <input v-model="formData.woundSize" type="text" placeholder="aktuelle Größe" class="p-2 border rounded w-full" />
          <input v-model="formData.woundGrade" type="text" placeholder="aktueller Grad" class="p-2 border rounded w-full" />
        </div>
        <input v-model="formData.woundMaterials" type="text" placeholder="Präparate, Verbandmaterialien" class="p-2 border rounded w-full mb-2" />
        <div class="grid grid-cols-3 gap-4">
          <div class="col-span-2 flex items-center">
            <input v-model="formData.woundAcute" type="checkbox" class="mr-2" />
            <label>Wundversorgung akut</label>
          </div>
          <FrequencyAndDuration 
            :items="formData.woundAcuteFrequency"
            @update="updateFrequencyAndDuration('woundAcuteFrequency', $event)"
          />
        </div>
        <div class="grid grid-cols-3 gap-4 mt-2">
          <div class="col-span-2 flex items-center">
            <input v-model="formData.woundChronic" type="checkbox" class="mr-2" />
            <label>Wundversorgung chronisch</label>
          </div>
          <FrequencyAndDuration 
            :items="formData.woundChronicFrequency"
            @update="updateFrequencyAndDuration('woundChronicFrequency', $event)"
          />
        </div>
      </div>

      <div class="mb-4">
        <h4 class="font-semibold">Sonstige Maßnahmen der Behandlungspflege</h4>
        <div class="grid grid-cols-3 gap-4 mb-2">
          <div class="col-span-2">
            <input v-model="formData.otherMeasures1" type="text" class="p-2 border rounded w-full" />
          </div>
          <FrequencyAndDuration 
            :items="formData.otherMeasures1Frequency"
            @update="updateFrequencyAndDuration('otherMeasures1Frequency', $event)"
          />
        </div>
        <div class="grid grid-cols-3 gap-4 mb-2">
          <div class="col-span-2">
            <input v-model="formData.otherMeasures2" type="text" class="p-2 border rounded w-full" />
          </div>
          <FrequencyAndDuration 
            :items="formData.otherMeasures2Frequency"
            @update="updateFrequencyAndDuration('otherMeasures2Frequency', $event)"
          />
        </div>
        
        <div class="mt-2">
          <h5 class="font-semibold text-sm">Anleitung zur Behandlungspflege für Patient/Angehörige</h5>
          <div class="grid grid-cols-3 gap-4">
            <div class="col-span-2 flex items-center space-x-2">
              <input v-model="formData.patientInstruction" type="text" class="p-2 border rounded flex-grow" />
              <input v-model="formData.patientInstructionCount" type="number" placeholder="Anzahl" class="p-2 border rounded w-20" />
            </div>
            <FrequencyAndDuration 
              :items="formData.patientInstructionFrequency"
              @update="updateFrequencyAndDuration('patientInstructionFrequency', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mb-4 border-t-2 border-red-500 pt-4">
      <h3 class="font-bold text-lg mb-2">Grundpflege und hauswirtschaftliche Versorgung</h3>
      <div class="grid grid-cols-2 gap-2">
        <label class="flex items-center">
          <input v-model="formData.basicCare" type="checkbox" class="mr-2" />
          Unterstützungspflege nach § 37 (1a) SGB V
        </label>
        <label class="flex items-center">
          <input v-model="formData.preventiveCare" type="checkbox" class="mr-2" />
          Krankenhausvermeidungspflege nach § 37 (1) SGB V
        </label>
      </div>
      <div class="grid grid-cols-3 gap-4 mt-2">
        <div class="col-span-2 flex items-center">
          <input v-model="formData.basicCareDetails" type="checkbox" class="mr-2" />
          <label>Grundpflege</label>
        </div>
        <FrequencyAndDuration 
          :items="formData.basicCareFrequency"
          @update="updateFrequencyAndDuration('basicCareFrequency', $event)"
        />
      </div>
      <div class="grid grid-cols-3 gap-4 mt-2">
        <div class="col-span-2 flex items-center">
          <input v-model="formData.householdCare" type="checkbox" class="mr-2" />
          <label>hauswirtschaftliche Versorgung</label>
        </div>
        <FrequencyAndDuration 
          :items="formData.householdCareFrequency"
          @update="updateFrequencyAndDuration('householdCareFrequency', $event)"
        />
      </div>
    </div>

    <div class="mb-4">
      <h4 class="font-semibold">Weitere Hinweise</h4>
      <textarea v-model="formData.additionalNotes" class="p-2 border rounded w-full" rows="3"></textarea>
    </div>

    <button @click="saveForm" class="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">Speichern</button>
  </div>
</template>

<script>
import { useERezeptStore } from '../../stores/eRezeptStore';
import { useDbStore } from '../../stores/dbStore';
import { storeToRefs } from 'pinia';
import FrequencyAndDuration from './FrequencyAndDuration.vue';

export default {
  name: 'Muster12Form',
  components: {
    FrequencyAndDuration
  },
  emits: ['save'],
  
  setup(props, { emit }) {
    const eRezeptStore = useERezeptStore();
    const dbStore = useDbStore();
    const { muster12FormData } = storeToRefs(eRezeptStore);

    const updateFrequencyAndDuration = (field, { index, item }) => {
      muster12FormData.value[field][index] = item;
    };

    const saveForm = async () => {
      await dbStore.createMuster12Bundle();
      emit('save');
    };

    return {
      formData: muster12FormData,
      updateFrequencyAndDuration,
      saveForm
    };
  }
};
</script>

<style scoped>
@media (max-width: 640px) {
  .frequency-and-duration {
    font-size: 0.75rem;
  }
}
</style>